// Centralizovany role / oprávnění helper pro frontend.
//
// Drzime tady jediny zdroj pravdy o tom, kdo co smi v UI — tlacitka, formulare,
// gating sidebar polozek. Cilem je shoda s `docs/PROJEKT.md` (sekce "Pristup a
// role uzivatelu") a s backendem (AuthorizationPolicies + ReportPolicy).
//
// Pouziti:
//   const { canOdeslatKeSchvaleni, canPisatPole } = usePermissions();
//   if (canOdeslatKeSchvaleni(op.stav)) ...
//
// Helper je framework-light: pouze cista funkce + maly hook nad AuthContextem.

import { useMemo } from 'react';
import { useAuth } from '@/lib/AuthContext';
import type { Role, StavOperativy, User } from '@/types/api';

/** Pole reportu pro permission gating (mirror backend ReportField enumu). */
export type ReportField =
  | 'vykon'
  | 'ukony'
  | 'material'
  | 'zjisteni'
  | 'kontrola'
  | 'stav';

/**
 * Vraci `true`, pokud user (z AuthContextu) ma alespon jednu z `roles`.
 * `null` user vraci `false`.
 */
export function hasAnyRole(user: User | null, roles: ReadonlyArray<Role>): boolean {
  if (!user || !user.roles) return false;
  return user.roles.some((r) => roles.includes(r));
}

/**
 * Stavy operativy, ve kterych je povoleny report (mirror ReportPolicy.MuzeZapisovatVeStavu).
 * Vcetne UZAVRENO — material a reporty se dopisuji zpetne a schvaleni to nerusi.
 */
export const STAVY_S_REPORTEM: StavOperativy[] = ['SCHVALENO', 'V_REALIZACI', 'UPRAVENA', 'UZAVRENO'];

/**
 * Schvalene stavy, kdy lze u polozky menit mechanizaci a zamestnance bez noveho schvaleni
 * (schvaluje se jen popis cinnosti). Mirror PolozkaService.PoSchvaleni.
 */
const STAVY_PO_SCHVALENI: StavOperativy[] = ['SCHVALENO', 'V_REALIZACI', 'UZAVRENO'];

/** Stavy operativy, ve kterych jeste muzeme menit plan (polozky / poznamka). */
const STAVY_S_EDITACI_NAVRHU: StavOperativy[] = ['NAVRH', 'UPRAVENA'];

export interface Permissions {
  /** Aktualne prihlaseny uzivatel (nebo null). */
  user: User | null;

  // ---- "Surove" role checky -------------------------------------------------
  isAdmin: boolean;
  isVedouci: boolean;
  isSchvalovatel: boolean;
  isTechnikInt: boolean;
  isTechnikExt: boolean;

  // ---- Strana ---------------------------------------------------------------
  isInterni: boolean;
  isExterni: boolean;

  // ---- "Mam alespon nejakou roli z mnoziny" helpers ------------------------
  hasRole: (role: Role) => boolean;
  hasAny: (roles: ReadonlyArray<Role>) => boolean;

  // ---- Action-level gating --------------------------------------------------
  /** Plan operativy lze upravovat (polozky) — VEDOUCI/ADMIN ve stavu NAVRH/UPRAVENA. */
  canEditNavrh: (stav: StavOperativy) => boolean;

  /**
   * Poznamku operativy lze upravovat — VEDOUCI/ADMIN ve stavu NAVRH/UPRAVENA,
   * nebo TECHNIK_EXT ve stavech reportu (SCHVALENO/V_REALIZACI/UPRAVENA).
   */
  canEditPoznamka: (stav: StavOperativy) => boolean;

  /** Tlacitko "Odeslat ke schvaleni" — VEDOUCI/ADMIN ve stavu NAVRH/UPRAVENA. */
  canOdeslatKeSchvaleni: (stav: StavOperativy) => boolean;

  /**
   * Tlacitko "Schvalit/Zamitnout" — role nasi firmy (SCHVALOVATEL/ADMIN, a TECHNIK_INT,
   * ktery muze byt zastupcem schvalovatele) ve stavu KE_SCHVALENI. Mirror backend policy
   * `CanRozhodovatSchvaleni`. Per-radek kontrola (zda je user opravnen rozhodnout za
   * konkretniho schvalovatele vc. zastupu) bezi v backendu pres OveritOpravneniAsync
   * a do UI se propaguje priznakem `muzeRozhodnout` na radku — tady gating jen UI.
   */
  canSchvalovat: (stav: StavOperativy) => boolean;

  /**
   * Uprava polozky po schvaleni (mechanizace, zamestnanci; popis zamceny) —
   * VEDOUCI/ADMIN ve stavu SCHVALENO/V_REALIZACI/UZAVRENO.
   */
  canUpravitPoSchvaleni: (stav: StavOperativy) => boolean;

  /** Tlacitko "Otevrit k uprave" — VEDOUCI/ADMIN ve stavu KE_SCHVALENI/SCHVALENO/V_REALIZACI/UZAVRENO. */
  canOtevritKUprave: (stav: StavOperativy) => boolean;

  /** Tlacitko "Uzavrit den" — VEDOUCI/ADMIN ve stavu SCHVALENO/V_REALIZACI. */
  canUzavritDen: (stav: StavOperativy) => boolean;

  /** Tlacitko "Smazat operativu" — VEDOUCI/ADMIN ve stavu NAVRH/UPRAVENA. */
  canSmazatOperativu: (stav: StavOperativy) => boolean;

  /** Tlacitka "Nova operativa" / "Kopirovat" / "Nova polozka" — VEDOUCI/ADMIN. */
  canVytvoritOperativu: boolean;
  canKopirovatOperativu: boolean;

  /**
   * Sprava ciselniku — mechanizace i cinnosti (nova / upravit / deaktivovat) —
   * VEDOUCI/ADMIN. Mirror backend policy `CanSpravovatCiselniky`.
   * Seznam vidi kazdy prihlaseny, gatovany je jen zapis.
   */
  canSpravovatCiselniky: boolean;

  /**
   * Sprava zastupu schvalovatelu (zalozit / upravit / zrusit) — SCHVALOVATEL (vlastni)
   * nebo ADMIN (vsechny). Pravidlo "jen vlastni vs. vsechny" vynucuje backend per zaznam.
   */
  canSpravovatZastupy: boolean;

  /**
   * Zobrazeni stranky Zastupy — navic TECHNIK_INT, ktery muze byt zastupcem a ma
   * videt, koho a kdy zastupuje. Mirror backend policy `CanRozhodovatSchvaleni`
   * na controlleru /api/zastupy.
   */
  canVidetZastupy: boolean;

  // ---- Report fields --------------------------------------------------------
  /**
   * Smi user zapisovat dane pole reportu? Mirror backend ReportPolicy.MuzeEditovatPole.
   *
   * - vykon, ukony: VEDOUCI/ADMIN
   * - material: VEDOUCI/TECHNIK_EXT/ADMIN
   * - zjisteni: VEDOUCI/TECHNIK_EXT/TECHNIK_INT/SCHVALOVATEL/ADMIN
   * - kontrola: TECHNIK_INT/SCHVALOVATEL/ADMIN (jen NASE_FIRMA — NIKDY externi)
   * - stav: VEDOUCI/TECHNIK_EXT/ADMIN
   *
   * `stavOperativy` muze stav v UI vypnout — pokud je operativa v NAVRH/KE_SCHVALENI/UZAVRENO,
   * vraci false (zapis reportu je az od SCHVALENO).
   */
  canPisatPole: (field: ReportField, stavOperativy: StavOperativy) => boolean;

  /** Boolean varianta bez kontroly stavu — pro pouziti pri vyrenderovani sekce. */
  muzeEditovatPole: (field: ReportField) => boolean;
}

/** Hook pro pouziti v React komponentach. */
export function usePermissions(): Permissions {
  const { user } = useAuth();
  return useMemo(() => buildPermissions(user), [user]);
}

/** Cista funkce pro pouziti mimo Reactovy stromy / v testech. */
export function buildPermissions(user: User | null): Permissions {
  const isAdmin = hasAnyRole(user, ['ADMIN']);
  const isVedouci = hasAnyRole(user, ['VEDOUCI']);
  const isSchvalovatel = hasAnyRole(user, ['SCHVALOVATEL']);
  const isTechnikInt = hasAnyRole(user, ['TECHNIK_INT']);
  const isTechnikExt = hasAnyRole(user, ['TECHNIK_EXT']);

  const isInterni = user?.strana === 'NASE_FIRMA';
  const isExterni = user?.strana === 'EXTERNI_FIRMA';

  const hasRole = (role: Role) => hasAnyRole(user, [role]);
  const hasAny = (roles: ReadonlyArray<Role>) => hasAnyRole(user, roles);

  const muzeEditovatPole = (field: ReportField): boolean => {
    if (isAdmin) return true;
    switch (field) {
      case 'vykon':
      case 'ukony':
        return isVedouci;
      case 'material':
        return isVedouci || isTechnikExt;
      case 'zjisteni':
        // Dle PROJEKT.md sekce 3 + tabulka roli: zjisteni doplnuje technik
        // externi/vedouci a uzivatele nasi firmy (TECHNIK_INT, SCHVALOVATEL).
        return isVedouci || isTechnikExt || isTechnikInt || isSchvalovatel;
      case 'kontrola':
        // Externi firma DO POLE KONTROLA NIKDY NEZAPISUJE. Hard guard pres `isInterni`.
        return isInterni && (isTechnikInt || isSchvalovatel);
      case 'stav':
        // Stav polozky meni ten, kdo realne provadi praci — VEDOUCI nebo TECHNIK_EXT.
        // (PHP zdroj ReportPolicy::POLE_STAV; mirror v backendu Backend/.../ReportPolicy.cs.)
        return isVedouci || isTechnikExt;
      default:
        return false;
    }
  };

  const canPisatPole = (field: ReportField, stavOperativy: StavOperativy): boolean =>
    STAVY_S_REPORTEM.includes(stavOperativy) && muzeEditovatPole(field);

  const canEditNavrh = (stav: StavOperativy) =>
    (isVedouci || isAdmin) && STAVY_S_EDITACI_NAVRHU.includes(stav);

  const canUpravitPoSchvaleni = (stav: StavOperativy) =>
    (isVedouci || isAdmin) && STAVY_PO_SCHVALENI.includes(stav);

  // Poznamka operativy: VEDOUCI/ADMIN kdykoli mimo KE_SCHVALENI (neni soucast
  // schvalovaneho planu), TECHNIK_EXT behem realizace (stejne stavy jako jeho report).
  // Mirror backend OperativaService.MuzeEditovatPoznamku.
  const canEditPoznamka = (stav: StavOperativy) =>
    ((isVedouci || isAdmin) && stav !== 'KE_SCHVALENI')
    || (isTechnikExt && STAVY_S_REPORTEM.includes(stav));

  const canOdeslatKeSchvaleni = (stav: StavOperativy) =>
    (isVedouci || isAdmin) && (stav === 'NAVRH' || stav === 'UPRAVENA');

  // TECHNIK_INT muze byt zastupcem schvalovatele — tlacitka se mu ukazou jen na
  // radcich, kde server vrati `muzeRozhodnout` (viz OperativaDetailPage).
  const canSchvalovat = (stav: StavOperativy) =>
    (isSchvalovatel || isAdmin || isTechnikInt) && stav === 'KE_SCHVALENI';

  const canOtevritKUprave = (stav: StavOperativy) =>
    (isVedouci || isAdmin)
    && (stav === 'KE_SCHVALENI'
      || stav === 'SCHVALENO'
      || stav === 'V_REALIZACI'
      || stav === 'UZAVRENO');

  const canUzavritDen = (stav: StavOperativy) =>
    (isVedouci || isAdmin) && (stav === 'V_REALIZACI' || stav === 'SCHVALENO');

  const canSmazatOperativu = (stav: StavOperativy) =>
    (isVedouci || isAdmin) && (stav === 'NAVRH' || stav === 'UPRAVENA');

  const canVytvoritOperativu = isVedouci || isAdmin;
  const canKopirovatOperativu = isVedouci || isAdmin;

  // Ciselniky spravuji obe strany (PROJEKT.md sekce "Ciselniky") — za externi
  // firmu vedouci, za nasi admin. Jeden helper pro mechanizaci i cinnosti.
  const canSpravovatCiselniky = isVedouci || isAdmin;

  // Zastupy schvalovatelu — schvalovatel spravuje sve, admin vsechny; technik
  // nasi firmy (mozny zastupce) je jen vidi.
  const canSpravovatZastupy = isSchvalovatel || isAdmin;
  const canVidetZastupy = canSpravovatZastupy || isTechnikInt;

  return {
    user,
    isAdmin,
    isVedouci,
    isSchvalovatel,
    isTechnikInt,
    isTechnikExt,
    isInterni,
    isExterni,
    hasRole,
    hasAny,
    canEditNavrh,
    canEditPoznamka,
    canOdeslatKeSchvaleni,
    canSchvalovat,
    canUpravitPoSchvaleni,
    canOtevritKUprave,
    canUzavritDen,
    canSmazatOperativu,
    canVytvoritOperativu,
    canKopirovatOperativu,
    canSpravovatCiselniky,
    canSpravovatZastupy,
    canVidetZastupy,
    canPisatPole,
    muzeEditovatPole,
  };
}
